import type { CustomFacetValue, ManufacturerFacet, ProductSearchResult } from '@model/product'
import { createContext, useContext } from 'react'
import type { SortOrder } from '@model/product/ProductSearchResult'
import type { CustomFacet } from '@model/product/CustomFacet'
import type { ImagePageBanner } from '@api/content'

export enum CustomFilter {
  Campaign = 9,
}

export interface FilterValue {
  id: string
  name: string
  group?: string
  query: string
}

export type CustomFilterMap = {
  [key in CustomFilter]: FilterValue[]
}

export type EncodeBrandFilterHandler = (brand: ManufacturerFacet) => FilterValue
export type DecodeBrandFilterHandler = (query: string) => FilterValue
export type ToggleBrandFilterHandler = (brand: ManufacturerFacet | FilterValue) => void
export type IsBrandFilterEnabledHandler = (brand: ManufacturerFacet | FilterValue) => boolean
export type GetBrandDataHandler = (brand: FilterValue) => ManufacturerFacet | null
export type SetCategoryDataHandler = (categoryData: ProductSearchResult | null) => void
export type SetCategoryBannersHandler = (categoryBanners: ImagePageBanner[]) => void
export type SetSortingOption = (sort: SortOrder) => void
export type EncodeCustomFilterHandler = (facet: CustomFacet, value: CustomFacetValue) => FilterValue
export type DecodeCustomFilterHandler = (query: string) => FilterValue
export type ToggleCustomFilterHandler = (facet: FilterValue) => void
export type IsCustomFilterEnabledHandler = (facet: FilterValue) => boolean

export interface ICategoryFiltersContext {
  brands: FilterValue[]
  custom: Partial<CustomFilterMap>
  brandsData: ManufacturerFacet[]
  getBrandData: GetBrandDataHandler
  encodeBrandFilter: EncodeBrandFilterHandler
  decodeBrandFilter: DecodeBrandFilterHandler
  toggleBrandFilter: ToggleBrandFilterHandler
  isBrandFilterEnabled: IsBrandFilterEnabledHandler
  sortingOption?: SortOrder
  queryParams: string
  setCategoryData: SetCategoryDataHandler
  setCategoryBanners: SetCategoryBannersHandler
  setSortOrder: SetSortingOption
  customs: FilterValue[]
  // setCustoms: FilterValue[]
  encodeCustomFilter: EncodeCustomFilterHandler
  decodeCustomFilter: DecodeCustomFilterHandler
  toggleCustom: ToggleCustomFilterHandler
  isCustomFilterEnabled: IsCustomFilterEnabledHandler
  minPrice: number | null
  setMinPrice: (newValue: number | null) => void
  maxPrice: number | null
  setMaxPrice: (newValue: number | null) => void
  resetAll(): void
}

export const CategoryFiltersContext = createContext<ICategoryFiltersContext>({
  brands: [],
  custom: {},
  banners: [],
  encodeBrandFilter: () => {},
  decodeBrandFilter: () => {},
  toggleBrandFilter: () => {},
} as any)

export function useCategoryFilters() {
  return useContext(CategoryFiltersContext)
}

export const isFilterValue = <T = any>(value: T | FilterValue): value is FilterValue => {
  return (
    (<FilterValue>value).id !== undefined &&
    (<FilterValue>value).name !== undefined &&
    (<FilterValue>value).query !== undefined
  )
}
